import type { BaseQueryFn, TypedMutationOnQueryStarted } from "@reduxjs/toolkit/query";
import { credentialsReceived, signedOut, type PortalCredentials } from "../features/auth/authSlice";
import { gboApi, portalAuth } from "./gboApiBase";
import { selectPortalCredentials } from "../features/auth/authSelectors";
import { portalIdentity } from "../features/auth/portalIdentity";
import { selectTenantId } from "../features/tenant/tenantSelectors";
import type { RootState } from "./store";

export type VerificationChallenge = {
  challengeId: string;
  expiresAt: string;
  resendAvailableAt: string;
};

export type AccessTokenContext = {
  kind: "accessToken";
  token: string;
};
export type InvoiceProofContext = {
  kind: "invoiceProof";
  invoiceCode: string;
  billingZip: string;
  invoiceTotal: number;
};
export type OnboardingContext = AccessTokenContext | InvoiceProofContext;

export type OnboardingDetails = {
  customerId: string;
  emails: string[];
  requiresVerification: boolean;
};

export type PortalAccount = {
  customerId: string;
  name: string;
  code: string;
  serviceAddress: string;
  balance: number;
  matchedEmails: string[];
};

export type PortalUser = {
  tenantId: string;
  timezone: string;
  subdomain: string;
  refreshTokenId: string;
  verifiedEmails: string[];
  customerId: string | null;
};

const saveCredentials: TypedMutationOnQueryStarted<
  PortalCredentials,
  unknown,
  BaseQueryFn
> = async (_arg, { dispatch, queryFulfilled }) => {
  try {
    const { data } = await queryFulfilled;
    dispatch(credentialsReceived(data));
  } catch {
    // If this fails, the caller will catch the error from the original request
  }
};

export const portalAuthApi = gboApi.injectEndpoints({
  endpoints: (builder) => ({
    getOnboardingDetails: builder.query<OnboardingDetails, OnboardingContext>({
      query: (context) => ({ url: "auth/onboarding-details", method: "POST", body: context }),
      keepUnusedDataFor: 0,
    }),
    startPortalVerification: builder.mutation<
      VerificationChallenge,
      { email: string; context?: OnboardingContext }
    >({
      query: ({ email, context }) => ({
        url: context ? "auth/verification-with-context" : "auth/verification",
        method: "POST",
        body: context ? { email, context } : { email },
      }),
    }),
    resendPortalVerification: builder.mutation<VerificationChallenge, { challengeId: string }>({
      query: ({ challengeId }) => ({
        url: `auth/verification/${encodeURIComponent(challengeId)}/resend`,
        method: "POST",
        body: {},
      }),
    }),
    signInPortal: builder.mutation<
      PortalCredentials,
      {
        challengeId?: string;
        code?: string;
        context?: OnboardingContext;
      }
    >({
      query: (request) => ({
        url: request.context ? "auth/sign-in-with-context" : "auth/sign-in",
        method: "POST",
        body: request.context
          ? { challengeId: request.challengeId, code: request.code, context: request.context }
          : { challengeId: request.challengeId, code: request.code },
      }),
      onQueryStarted: saveCredentials,
    }),
    addPortalVerifiedEmail: builder.mutation<
      PortalCredentials,
      { challengeId: string; code: string }
    >({
      query: (request) => ({ url: "auth/verified-emails", method: "POST", body: request }),
      onQueryStarted: saveCredentials,
    }),
    getPortalUser: builder.query<PortalUser, void>({
      query: () => ({ url: "auth/me" }),
      keepUnusedDataFor: 0,
    }),
    getPortalAccounts: builder.query<PortalAccount[], string | void>({
      query: () => ({ url: "auth/accounts" }),
      keepUnusedDataFor: 0,
    }),
    selectPortalAccount: builder.mutation<PortalCredentials, { customerId: string }>({
      queryFn: async (request, api, _extra, baseQuery) => {
        const startingState = api.getState() as RootState;
        const tenantId = selectTenantId(startingState);
        const startingCredentials = selectPortalCredentials(startingState);
        const result = await baseQuery({
          url: "auth/select-account",
          method: "POST",
          body: request,
        });
        const currentState = api.getState() as RootState;
        const currentCredentials = selectPortalCredentials(currentState);
        const selectionIsCurrent =
          selectTenantId(currentState) === tenantId &&
          portalIdentity.isSameScope(startingCredentials, currentCredentials);
        if (result.error) {
          if (selectionIsCurrent) {
            const restored = await portalAuth.refreshCredentials(api);
            const latestState = api.getState() as RootState;
            if (
              "error" in restored &&
              selectTenantId(latestState) === tenantId &&
              selectPortalCredentials(latestState) === currentCredentials
            )
              api.dispatch(signedOut());
          }
          return { error: result.error };
        }
        const data = result.data as PortalCredentials;
        if (selectionIsCurrent) api.dispatch(credentialsReceived(data));
        return { data };
      },
    }),
    logoutPortal: builder.mutation<{ success: boolean }, void>({
      query: () => ({ url: "auth/logout", method: "POST", body: {} }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(signedOut());
        } catch {}
      },
    }),
  }),
});

export const {
  useLazyGetOnboardingDetailsQuery,
  useStartPortalVerificationMutation,
  useResendPortalVerificationMutation,
  useSignInPortalMutation,
  useAddPortalVerifiedEmailMutation,
  useLazyGetPortalUserQuery,
  useLogoutPortalMutation,
  useLazyGetPortalAccountsQuery,
  useSelectPortalAccountMutation,
  useGetPortalAccountsQuery,
} = portalAuthApi;
